export interface CropConstraint {
  /** Width / height ratio. undefined = free crop. */
  aspect: number | undefined;
  minWidth: number;
  minHeight: number;
  shape: "rect" | "round";
  label: string;
}

/**
 * Returns the crop constraint for a given section type + field name,
 * or null if the field has no crop constraint defined.
 */
export function getCropConstraint(
  sectionType: string,
  fieldName: string,
  extraContext?: { layout?: string; imageAspect?: string },
): CropConstraint | null {
  const layout = extraContext?.layout;

  switch (sectionType) {
    case "Hero":
      if (fieldName === "imageUrl" && layout === "split") {
        return {
          aspect: 4 / 3,
          minWidth: 600,
          minHeight: 450,
          shape: "rect",
          label: "Hero image -- 4:3, min 600x450",
        };
      }
      if (fieldName === "backgroundImageUrl" && layout === "image") {
        return {
          aspect: 16 / 9,
          minWidth: 1200,
          minHeight: 675,
          shape: "rect",
          label: "Background -- 16:9, min 1200x675",
        };
      }
      return null;

    case "ImageText":
      if (fieldName === "imageUrl") {
        return {
          aspect: imageTextAspect(extraContext?.imageAspect),
          minWidth: 400,
          minHeight: 300,
          shape: "rect",
          label: imageTextLabel(extraContext?.imageAspect),
        };
      }
      return null;

    case "TeamGrid":
      if (fieldName === "photoUrl") {
        return {
          aspect: 1,
          minWidth: 400,
          minHeight: 400,
          shape: "round",
          label: "Headshot -- square, min 400x400",
        };
      }
      return null;

    case "FeatureShowcase":
      if (fieldName === "imageUrl") {
        return {
          aspect: 4 / 3,
          minWidth: 600,
          minHeight: 450,
          shape: "rect",
          label: "Feature image -- 4:3, min 600x450",
        };
      }
      return null;

    case "LogoStrip":
      if (fieldName === "imageUrl") {
        return {
          aspect: undefined,
          minWidth: 100,
          minHeight: 24,
          shape: "rect",
          label: "Logo -- any ratio, min 100px wide",
        };
      }
      return null;

    case "Testimonials":
      if (fieldName === "avatarUrl") {
        return {
          aspect: 1,
          minWidth: 200,
          minHeight: 200,
          shape: "round",
          label: "Avatar -- square, min 200x200",
        };
      }
      return null;

    case "BlogFeaturedImage":
      if (fieldName === "featuredImageUrl") {
        return {
          aspect: 16 / 9,
          minWidth: 1200,
          minHeight: 675,
          shape: "rect",
          label: "Featured image -- 16:9, min 1200x675",
        };
      }
      return null;

    default:
      return null;
  }
}

/* ── ImageText dynamic aspect helpers ─────────────────────── */

function imageTextAspect(imageAspect: string | undefined): number | undefined {
  switch (imageAspect) {
    case "4:3":
      return 4 / 3;
    case "16:9":
      return 16 / 9;
    case "1:1":
      return 1;
    case "auto":
    default:
      return undefined;
  }
}

function imageTextLabel(imageAspect: string | undefined): string {
  switch (imageAspect) {
    case "4:3":
      return "Image -- 4:3, min 400x300";
    case "16:9":
      return "Image -- 16:9, min 400x300";
    case "1:1":
      return "Image -- square, min 400x300";
    case "auto":
    default:
      return "Image -- any ratio, min 400x300";
  }
}
